import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, InputBase, IconButton, Tooltip, Chip, useTheme, alpha } from "@mui/material";
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  sendContractMessage,
  clearContractMessages,
  createContractConversation
} from '../../../../redux/actions/contractAction';
import { backendClient } from "../../../../api/backend";
import ReactGA from 'react-ga4';
import { tracker } from '../../../../analytics';
import ContractMessage from './ContractMessage';


const PRESET_ICONS = [
  <ArticleOutlinedIcon sx={{ fontSize: 13 }} />,
  <VpnKeyOutlinedIcon sx={{ fontSize: 13 }} />,
  <FactCheckOutlinedIcon sx={{ fontSize: 13 }} />,
];

const ChatBot = ({ conversationId: propConversationId, contractId, userId }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { t } = useTranslation();
  const isDark = theme.palette.mode === 'dark';

  const messages = useSelector((state) => state.contract.messages || []);
  const currentConversationId = useSelector((state) => state.contract.currentConversationId);
  const contractCountry = useSelector(state => state.contract?.contract?.country);
  const userCountryPref = useSelector(state => state.user?.countryPreference);
  const countryPreference = contractCountry
    || (Array.isArray(userCountryPref) ? (userCountryPref[0] || 'MA') : (userCountryPref || 'MA'));

  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(propConversationId || currentConversationId);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const chatEndRef = useRef(null);
  const eventSourceRef = useRef(null);

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (isInitialized) return;
    if (propConversationId) {
      setConversationId(propConversationId);
      setIsInitialized(true);
    } else if (currentConversationId) {
      setConversationId(currentConversationId);
      setIsInitialized(true);
    } else {
      createNewConversation();
    }
  }, [dispatch, propConversationId, currentConversationId, isInitialized]);

  const createNewConversation = async () => {
    try {
      setError(null);
      const response = dispatch(createContractConversation(contractId, userId));
      if (response && response.conversation_id) {
        setConversationId(response.conversation_id);
        setIsInitialized(true);
      }
    } catch (err) {
      setError(t('chatbot.errorMessage.conversation'));
    } finally {
      setLoading(false);
      ReactGA.event({ category: 'Contract Chat', action: 'Started New Conversation' });
      tracker.event({ category: 'Contract Chat', action: 'Started New Conversation' });
    }
  };

  const sendMessage = async (text) => {
    if (!text.trim() || !conversationId || loading) return;
    setLoading(true);
    if (eventSourceRef.current) eventSourceRef.current.close();
    const source = await dispatch(
      sendContractMessage(conversationId, contractId, userId, text.trim(), countryPreference)
    );
    eventSourceRef.current = source;
    setInput("");
    setLoading(false);
    ReactGA.event({ category: 'Contract Chat', action: 'Sent Message', label: 'User message' });
    tracker.event({ category: 'Contract Chat', action: 'Sent Message', label: 'User message' });
  };

  const handleClearChat = async () => {
    if (!conversationId || loading) return;
    try {
      setLoading(true);
      setError(null);
      await backendClient.clearContractConversation(conversationId);
      dispatch(clearContractMessages());
    } catch (err) {
      setError(t('chatbot.errorMessage.clearChat'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const isStreamingBotMessage = lastMessage && lastMessage.sender === "bot" && (lastMessage.status === "PENDING" || lastMessage.is_streaming);
  const isStreaming = isStreamingBotMessage;

  const presetButtons = [
    { label: t('chatbot.presetButtons.summarize'), icon: PRESET_ICONS[0], gaLabel: 'Summarize Contract' },
    { label: t('chatbot.presetButtons.keyTerms'), icon: PRESET_ICONS[1], gaLabel: 'Contract Key Terms' },
    { label: t('chatbot.presetButtons.checkConformity'), icon: PRESET_ICONS[2], gaLabel: 'Check Contract Conformity' },
  ];

  const isDisabled = loading || !conversationId || isStreaming;
  const canSend = input.trim() && !isDisabled;

  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>

      {/* ── Header ── */}
      <Box sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        pb: 1.5,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
        mb: 0,
        flexShrink: 0,
      }}>
        <Box>
          <Chip
            label={t("riskAnalysis.Chip")}
            size="small"
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.18 : 0.12),
              color: theme.palette.primary.main,
              fontWeight: 600,
              fontSize: '0.68rem',
              height: 20,
              mb: 0.75,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
              letterSpacing: '0.02em',
            }}
          />
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: theme.palette.text.primary, lineHeight: 1.2, letterSpacing: '-0.01em' }}>
            {t("chatbot.title")}
          </Typography>
        </Box>

        <Tooltip title={t('chatbot.clearChat')}>
          <span>
            <IconButton
              onClick={() => {
                ReactGA.event({ category: 'Contract Chat', action: 'Clicked Clear Chat' });
                tracker.event({ category: 'Contract Chat', action: 'Clicked Clear Chat' });
                handleClearChat();
              }}
              size="small"
              disabled={messages.length === 0 || loading || isStreaming}
              sx={{
                mt: 0.5,
                color: theme.palette.text.secondary,
                border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                borderRadius: '8px',
                width: 30,
                height: 30,
                transition: 'all 0.18s',
                '&:hover': {
                  color: theme.palette.error.main,
                  borderColor: alpha(theme.palette.error.main, 0.5),
                  backgroundColor: alpha(theme.palette.error.main, 0.07),
                },
                '&.Mui-disabled': { opacity: 0.3 },
              }}
            >
              <RefreshIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* ── Messages area ── */}
      <Box sx={{
        flexGrow: 1,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        py: 1.5,
        mx: -1,
        px: 1,
        '&::-webkit-scrollbar': { width: '3px' },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': {
          background: alpha(theme.palette.text.secondary, 0.18),
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: alpha(theme.palette.text.secondary, 0.35),
        },
      }}>
        {messages.length === 0 ? (
          <Box sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            gap: 2.5,
            py: 4,
          }}>
            <Box sx={{
              width: 52,
              height: 52,
              borderRadius: '14px',
              background: isDark
                ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.primary.main, 0.05)})`
                : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.03)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.2 : 0.15)}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.1)}`,
            }}>
              <img
                key={theme.palette.mode}
                src={isDark ? '/logo/logo-circle-dark-mode.svg' : '/logo/logo-circle.svg'}
                alt="Lawis Logo"
                style={{ width: '30px', height: '30px', objectFit: 'contain' }}
              />
            </Box>
            <Box sx={{ textAlign: 'center', maxWidth: 280, mx: 'auto' }}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', mb: 0.5, color: theme.palette.text.primary }}>
                {t('chatbot.noMessages.title', 'Welcome to Contract Assistant')}
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: theme.palette.text.secondary, lineHeight: 1.55 }}>
                {t('chatbot.noMessages.subtitle', 'Ask questions about your contract or use the preset buttons below to get started.')}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, width: '100%', maxWidth: 280 }}>
              {presetButtons.map(({ label, icon, gaLabel }) => (
                <Box
                  key={gaLabel}
                  component="button"
                  onClick={() => {
                    ReactGA.event({ category: 'Contract Chat', action: 'Clicked Preset Button', label: gaLabel });
                    tracker.event({ category: 'Contract Chat', action: 'Clicked Preset Button', label: gaLabel });
                    sendMessage(label);
                  }}
                  disabled={isDisabled}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    width: '100%',
                    height: 38,
                    px: 1.5,
                    fontSize: '0.78rem',
                    fontWeight: 500,
                    fontFamily: theme.typography.fontFamily,
                    letterSpacing: '0.01em',
                    textTransform: 'none',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    color: theme.palette.text.secondary,
                    border: `1px solid ${alpha(theme.palette.divider, isDark ? 0.5 : 0.7)}`,
                    backgroundColor: isDark ? alpha('#fff', 0.03) : '#fff',
                    transition: 'all 0.16s ease',
                    outline: 'none',
                    '&:hover:not(:disabled)': {
                      color: theme.palette.primary.main,
                      borderColor: alpha(theme.palette.primary.main, 0.4),
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    },
                    '&:active:not(:disabled)': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    },
                    '&:disabled': {
                      opacity: 0.35,
                      cursor: 'not-allowed',
                    },
                  }}
                >
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 26,
                    height: 26,
                    borderRadius: '7px',
                    backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.12 : 0.08),
                    color: theme.palette.primary.main,
                    flexShrink: 0,
                  }}>
                    {icon}
                  </Box>
                  <span>{label}</span>
                </Box>
              ))}
            </Box>
          </Box>
        ) : (
          messages.map((msg, index) => (
            <ContractMessage
              key={msg.id || index}
              message={msg}
              isLast={index === messages.length - 1}
            />
          ))
        )}

        {error && (
          <Box sx={{
            color: 'error.main',
            textAlign: 'center',
            mt: 1.5,
            backgroundColor: alpha(theme.palette.error.main, 0.08),
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
            p: 1.25,
            borderRadius: '8px',
          }}>
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{error}</Typography>
          </Box>
        )}

        {messages.length > 10 && (
          <Box sx={{
            textAlign: 'center',
            mt: 1.5,
            mb: 1,
            backgroundColor: alpha(theme.palette.warning.main, 0.08),
            p: 1,
            borderRadius: '8px',
            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
          }}>
            <Typography sx={{ color: theme.palette.warning.main, fontSize: '0.72rem' }}>
              {t('chatbot.performanceWarning')}
            </Typography>
          </Box>
        )}

        <div ref={chatEndRef} />
      </Box>

      {/* ── Input field ── */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        backgroundColor: isDark ? alpha('#fff', 0.05) : '#fff',
        border: `1.5px solid ${alpha(theme.palette.divider, isDark ? 0.5 : 0.7)}`,
        borderRadius: '10px',
        padding: '3px 3px 3px 12px',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        flexShrink: 0,
        '&:focus-within': {
          borderColor: alpha(theme.palette.primary.main, 0.7),
          boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.08)}`,
        },
      }}>
        <InputBase
          fullWidth
          placeholder={t('chatbot.placeholder')}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && sendMessage(input)}
          disabled={isDisabled}
          sx={{
            flex: 1,
            fontSize: '0.855rem',
            '& .MuiInputBase-input': {
              py: 0.5,
              '&::placeholder': {
                color: theme.palette.text.disabled,
                opacity: 1,
              },
            },
          }}
        />
        <IconButton
          onClick={() => sendMessage(input)}
          disabled={!canSend}
          size="small"
          sx={{
            width: 32,
            height: 32,
            borderRadius: '7px',
            backgroundColor: canSend ? theme.palette.primary.main : 'transparent',
            color: canSend ? (isDark ? '#000' : '#fff') : alpha(theme.palette.text.secondary, 0.4),
            transition: 'all 0.18s',
            '&:hover': {
              backgroundColor: canSend ? theme.palette.primary.dark : 'transparent',
            },
            '&.Mui-disabled': {
              backgroundColor: 'transparent',
              color: alpha(theme.palette.text.secondary, 0.3),
            },
          }}
        >
          <SendIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>

    </Box>
  );
};

export default ChatBot;
